import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  screenerCountQueryOptions,
  screenerEveryQueryOptions,
  screenerMetricsQueryOptions,
  screenerPageQueryOptions,
  screenerSectorsQueryOptions,
} from '@/features/screener/api/queries'
import {
  NO_FILTER,
  filterOf,
  parseScreenState,
  toScreenQuery,
} from '@/features/screener/lib/screenState'
import type { RouterContext } from '@/routes/layouts/root'

/**
 * The `/screener/advanced` loader.
 *
 * MAIN, awaited for the ENTRY URL: the metric list (the columns, the filters and the sorts), then
 * the page the URL asks for, which can only be built once the metrics say which keys are real.
 * No `loaderDeps`, so a filter, sort or page change re-runs nothing here: the screener re-keys its
 * own page read and draws its skeleton rows over the wait (docs/route-loading.md, "Search
 * params").
 *
 * SECONDARY, started and not awaited: the unfiltered count, the sector list, and on a heatmap
 * link every matching row.
 */
export async function loadAdvancedScreener({
  context: { queryClient },
  location,
}: {
  context: RouterContext
  location: { search: unknown }
}): Promise<void> {
  const state = parseScreenState(location.search)
  const metrics = await ensureRouteData(queryClient, screenerMetricsQueryOptions())
  const query = toScreenQuery(state, metrics)
  prefetchRouteData(queryClient, screenerCountQueryOptions(NO_FILTER))
  prefetchRouteData(queryClient, screenerSectorsQueryOptions())
  if (state.view === 'heatmap') {
    prefetchRouteData(queryClient, screenerEveryQueryOptions(filterOf(query), query.sort))
  }
  await ensureRouteData(queryClient, screenerPageQueryOptions(query))
}
